import { addDays, addMonths, isAfter, isBefore, isToday, isValid, parse, subDays, subMonths, subYears } from 'date-fns';

export const isValidImmatriculation = (value: string) => {
    if (!value) {
        return undefined;
    }
    // Nouvelle plaque immatriculation : AA123AA ou AA123A ou C254L
    if (/^[a-zA-Z]{1,2} ?-?[0-9]{2,3} ?-?[a-zA-Z]{1,2}$/.test(value)) {
        return undefined;
    }

    // Ancienne plaque immatriculation : 1234AA12
    if (/^[0-9]{1,4} ?-?[a-zA-Z]{2,3} ?-?[0-9]{2}$/.test(value)) {
        return undefined;
    }

    // Ancienne plaque corse : 1234AAA1A
    if (/^[0-9]{1,4} ?-?[a-zA-Z]{2,3} ?-?[0-9][a-bA-B]$/.test(value)) {
        return undefined;
    }

    // Format 123AA12 ou 123AAA12
    if (/^[0-9]{3}[a-zA-Z]{2,3}[0-9]{2}$/.test(value)) {
        return undefined;
    }

    // Format A12A
    if (/^[a-zA-Z][0-9]{2}[a-zA-Z]$/.test(value)) {
        return undefined;
    }

    return "La plaque d'immatriculation n'est pas valide";
};

export const isNotInTheFuture = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isAfter(parsedDate, new Date())) {
        return 'La date ne peut pas être supérieure à la date du jour';
    }

    return undefined;
};

export const isNotMoreThan31DaysInTheFuture = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isAfter(parsedDate, addDays(new Date(), 31))) {
        return 'La date ne peut pas dépasser 31 jours après la date du jour';
    }

    return undefined;
};

export const isOlderThan6Months = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isBefore(parsedDate, subMonths(new Date(), 6))) {
        return 'La date ne peut pas être supérieure à 6 mois';
    }

    return undefined;
};

export const isOlderThan30Years = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isBefore(parsedDate, subYears(new Date(), 30))) {
        return 'La date ne peut pas être supérieure à 30 ans';
    }

    return undefined;
};

export const isValidDate = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Utiliser isValid de date-fns pour vérifier si la date est valide
    if (isValid(parsedDate)) {
        return undefined; // La date est valide
    }

    return 'Remplissez une date valide';
};

export const isValidDateTime = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy HH:mm', new Date());

    // Utiliser isValid de date-fns pour vérifier si la date est valide
    if (isValid(parsedDate)) {
        return undefined; // La date est valide
    }

    return 'Remplissez une date valide';
};

export const isValidTime = (value: string) => {
    const parsedDate = parse(value, 'HH:mm', new Date());

    // Utiliser isValid de date-fns pour vérifier si la date est valide
    if (isValid(parsedDate)) {
        return undefined; // La date est valide
    }

    return 'Remplissez une heure valide';
};

export const isDateTimeNotInThePast = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy HH:mm', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isBefore(parsedDate, new Date())) {
        return 'La date ne peut pas être dans le passé';
    }

    return undefined;
};

export const isDateNotInThePast = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isBefore(parsedDate, new Date()) && !isToday(parsedDate)) {
        return 'La date ne peut pas être dans le passé';
    }

    return undefined;
};

export const isDateTimeNot30DaysInTheFuture = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy HH:mm', new Date());

    // Vérifie si la date est valide avant de comparer
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    if (isAfter(parsedDate, addDays(new Date(), 30))) {
        return 'La date ne peut pas être supérieure à 30 jours';
    }

    return undefined;
};

export const isWithinMonths = (months: number) => (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    const maxDate = addMonths(new Date(), months);
    if (isAfter(parsedDate, maxDate)) {
        return `Veuillez saisir une date sans dépasser les ${months} mois`;
    }

    return undefined;
};

export const isNotOlderThanMonths = (months: number) => (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    const minDate = subMonths(new Date(), months);
    if (isBefore(parsedDate, minDate)) {
        return `La date d'achat du véhicule ne peut pas être supérieure à ${months} mois`;
    }

    return undefined;
};

export const isPriceInRange = (min: number, max: number) => (value: number | string) => {
    const price = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(price)) {
        return 'Veuillez entrer un prix valide';
    }

    if (price < min) {
        return `La valeur d'achat du véhicule ne peut être inférieure à ${min}€`;
    }

    if (price > max) {
        return `Nous n'assurons pas les véhicules dont le prix d'achat est supérieur à ${max}€`;
    }

    return undefined;
};

export const isAgeInMonthsValid = (value: string | number) => {
    const age = typeof value === 'string' ? parseInt(value, 10) : value;

    if (isNaN(age)) {
        return 'Veuillez entrer un nombre valide';
    }

    if (age < 0) {
        return "L'âge du véhicule ne peut pas être négatif";
    }

    if (age > 120) {
        return "L'âge maximal éligible est de 120 mois";
    }

    return undefined;
};

export const isValidCodePostal = (value: string) => {
    if (!value) {
        return undefined;
    }

    // Vérifie si le code postal est un nombre à 5 chiffres
    if (/^\d{5}$/.test(value)) {
        return undefined;
    }

    // Vérifie si le code postal est un nombre à 4 chiffres suivi d'un espace et d'un chiffre
    if (/^\d{4} \d$/.test(value)) {
        return undefined;
    }

    return "Le code postal n'est pas valide";
};

export const isAdult = (value: string) => {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    // Vérifie si la date est valide
    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    // Date limite : il faut être né il y a au moins 18 ans
    const eighteenYearsAgo = subYears(new Date(), 18);

    if (isAfter(parsedDate, eighteenYearsAgo)) {
        return 'Vous devez avoir au moins 18 ans';
    }

    return undefined;
};

export const isAfter1900 = (value: string) => {
    if (!value) return undefined;

    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    if (!isValid(parsedDate)) {
        return 'Format de la date invalide';
    }

    const minDate = new Date(1900, 0, 1);

    if (isBefore(parsedDate, minDate)) {
        return 'La date de naissance doit être postérieure à 1900';
    }

    return undefined;
};

export const isPostalCodeAllowed = (value: string) => {
    if (!/^\d{5}$/.test(value)) {
        return 'Le code postal doit être composé de 5 chiffres';
    }

    const postalCode = parseInt(value, 10);

    // Monaco
    if (value.startsWith('980')) {
        return 'Nous ne sommes pas en mesure d’assurer les véhicules sur cette zone.';
    }

    // DROM - Vérifier d'abord les codes spécifiquement refusés (97100 à 97699 sauf 97133 et 97150)
    if (postalCode >= 97100 && postalCode <= 97699) {
        return 'Nous ne sommes pas en mesure d’assurer les véhicules sur cette zone.';
    }

    // Corse
    if (postalCode >= 20000 && postalCode <= 20999) {
        return undefined; // Accepté
    }

    // France métropolitaine
    if (postalCode >= 1000 && postalCode <= 95999) {
        return undefined; // Accepté
    }

    // Autres (étranger)
    return 'Nous ne sommes pas en mesure d’assurer les véhicules sur cette zone.';
};

export const isPostalCodeAllowedForDelivery = (value: string) => {
    if (!/^\d{5}$/.test(value)) {
        return 'Le code postal doit être composé de 5 chiffres';
    }

    const postalCode = parseInt(value, 10);

    // Zones interdites pour la livraison
    const forbiddenPrefixes = ['97', '98', '20'];
    if (forbiddenPrefixes.some((prefix) => value.startsWith(prefix))) {
        return 'Nous ne livrons pas dans cette zone géographique';
    }

    // Zones spécifiques de Marseille interdites
    const marseilleExcluded = [
        '13001',
        '13002',
        '13003',
        '13004',
        '13005',
        '13006',
        '13007',
        '13008',
        '13009',
        '13010',
        '13011',
        '13012',
        '13013',
        '13014',
        '13015',
        '13016',
    ];
    if (marseilleExcluded.includes(value)) {
        return 'Nous ne livrons pas dans cette zone de Marseille';
    }

    // Autres zones interdites pour la livraison
    if (value.startsWith('06') || value.startsWith('83')) {
        return 'Nous ne livrons pas dans cette zone géographique';
    }

    // France métropolitaine autorisée (hors zones exclues ci-dessus)
    if (postalCode >= 1000 && postalCode <= 95999) {
        return undefined; // Accepté
    }

    // Monaco et étranger
    return "Les adresses à l'étranger ne sont pas acceptées pour la livraison";
};

export const isValidPhoneNumber = (value: string) => {
    if (!value) {
        return 'Le numéro de téléphone est requis';
    }

    // Numéro français : commence par 0 + 9 chiffres, le 2e chiffre de 1 à 9
    if (/^0[1-9]\d{8}$/.test(value)) {
        return undefined;
    }

    return 'Le numéro de téléphone doit être un numéro français valide';
};

export const isValidEmail = (value: string) => {
    if (!value) {
        return "L'email est requis";
    }

    // Email simple (RFC 5322 basique, insensible à la casse)
    if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
        return undefined;
    }

    return 'Veuillez entrer une adresse email valide';
};

/**
 * Vérifie qu'un nom ou prénom ne contient pas de caractères spéciaux
 * (autorisés : lettres, accents, espaces, tirets, apostrophes)
 */
export const isValidPersonName = (value: string) => {
    if (!value) {
        return undefined;
    }

    if (/^[\p{L}\s'’-]+$/u.test(value)) {
        return undefined;
    }

    return 'Les caractères spéciaux (@, chiffres, etc.) ne sont pas autorisés';
};

// À ajouter dans packages/utils/lib/validation.ts
// (après les fonctions existantes)

/**
 * Vérifie que la date/heure n'est pas antérieure à une date de référence
 * Équivalent de isBeforeAv dans l'ancien formulaire Redux
 * @param referenceDateStr - Date de référence au format 'dd/MM/yyyy'
 */
export const isDateTimeNotBefore =
    (referenceDateStr: string) =>
    (value: string): string | undefined => {
        const parsedDate = parse(value, 'dd/MM/yyyy HH:mm', new Date());
        const referenceDate = parse(referenceDateStr, 'dd/MM/yyyy', new Date());

        if (!isValid(parsedDate)) {
            return 'Format de la date invalide';
        }

        if (!isValid(referenceDate)) {
            return undefined; // Si la référence est invalide, on ne bloque pas
        }

        // Compare uniquement les jours (sans l'heure) comme dans l'ancien isBeforeAv
        const parsedDateOnly = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
        const referenceDateOnly = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());

        if (isBefore(parsedDateOnly, referenceDateOnly)) {
            return "La date ne peut pas être antérieure à la date d'effet du contrat";
        }

        return undefined;
    };

/**
 * Vérifie que la date/heure n'est pas plus ancienne que X jours
 * Équivalent de minDate60Days dans l'ancien formulaire Redux
 * @param days - Nombre de jours maximum dans le passé
 */
export const isDateTimeNotOlderThanDays =
    (days: number) =>
    (value: string): string | undefined => {
        const parsedDate = parse(value, 'dd/MM/yyyy HH:mm', new Date());

        if (!isValid(parsedDate)) {
            return 'Format de la date invalide';
        }

        const minDate = subDays(new Date(), days);

        if (isBefore(parsedDate, minDate)) {
            return `La date ne peut pas être antérieure à ${days} jours`;
        }

        return undefined;
    };

/**
 * Vérifie que la date/heure n'est pas plus ancienne que X mois
 * Équivalent de minDate6Months dans l'ancien formulaire Redux
 * Note: Différent de isNotOlderThanMonths qui parse 'dd/MM/yyyy' sans l'heure
 * @param months - Nombre de mois maximum dans le passé
 */
export const isDateTimeNotOlderThanMonths =
    (months: number) =>
    (value: string): string | undefined => {
        const parsedDate = parse(value, 'dd/MM/yyyy HH:mm', new Date());

        if (!isValid(parsedDate)) {
            return 'Format de la date invalide';
        }

        const minDate = subMonths(new Date(), months);

        if (isBefore(parsedDate, minDate)) {
            return `La date ne peut pas être antérieure à ${months} mois`;
        }

        return undefined;
    };

/**
 * Vérifie que la longueur du numéro de marquage est entre 3 et 15 caractères
 * Équivalent de minLentghMarquage dans l'ancien formulaire Redux
 */
export const isValidMarquageLength = (value: string): string | undefined => {
    if (!value) {
        return undefined; // Champ optionnel
    }

    if (value.length < 3 || value.length > 15) {
        return 'Le numéro de marquage doit contenir entre 3 et 15 caractères';
    }

    return undefined;
};

/**
 * Valide le code postal et exclut les régions restreintes pour le vélo:
 * - Corse (20xxx)
 * - Monaco (98xxx)
 * - Territoires d'outre-mer/DROM (97xxx)
 *
 * Équivalent de excludeCodePostalWithCorse dans l'ancien formulaire-monorepo
 */
export const excludeRestrictedPostalCodes = (value: string | undefined): string | undefined => {
    if (!value) return undefined;

    const postalCode = value.trim();

    // Bloque Corse (20xxx)
    if (postalCode.startsWith('20')) {
        return "Nous ne sommes pas en mesure d'assurer les véhicules de cette région";
    }

    // Bloque Territoires d'outre-mer/DROM (97xxx)
    if (postalCode.startsWith('97')) {
        return "Nous ne sommes pas en mesure d'assurer les véhicules de cette région";
    }

    // Bloque Monaco (98xxx)
    if (postalCode.startsWith('98')) {
        return "Nous ne sommes pas en mesure d'assurer les véhicules de cette région";
    }

    return undefined;
};

/**
 * Valide qu'une valeur booléenne est définie (true ou false)
 * Utile pour les radio buttons Oui/Non où false est une réponse valide
 * @param message - Message d'erreur personnalisé
 */
export const isRequiredBoolean =
    (message = 'Ce champ est requis') =>
    (value: boolean | undefined | null): string | undefined => {
        if (value === undefined || value === null) {
            return message;
        }
        return undefined;
    };

/**
 * Valide qu'une valeur booléenne est true
 * Utile pour les cases à cocher obligatoires ou les déclarations
 * @param message - Message d'erreur si false ou non défini (accepte RenderedContent)
 */
export const mustBeTrue =
    (message: unknown) =>
    (value: boolean | undefined | null): string | undefined => {
        if (value !== true) {
            return message as string;
        }
        return undefined;
    };
